import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { rupees, time, type MyDay, type Queue, type Outstanding } from './myDayData';

export function QueryState({ error, retry, label }: { error?: string; retry: () => void; label: string; }) {
  return error ? <div role="alert" className="p-4 text-red-700">Could not load {label}. <button className="btn btn-sm" onClick={retry}>Retry</button>
  </div>
    : <p role="status" className="p-4">Loading {label}…</p>;
}
export function Section({ title, to, children }: { title: string; to: string; children: ReactNode; }) {
  return <section className="card p-4 min-w-0">
    <header className="mb-3 flex justify-between items-center">
      <h2 className="text-base font-semibold">{title}</h2>
      <Link to={to} className="text-sm underline">View all</Link>
    </header>{children}</section>;
}
export function CallQueue({ rows }: { rows: Queue; }) {
  return <Section title="Call queue" to="/telecaller">{rows.length === 0 ? <p>No calls waiting in your queue.</p> : <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr>{['Name', 'Source', 'City', 'Last touch', 'Priority', 'Action'].map(h => <th key={h} className="text-left p-2">{h}</th>)}</tr>
      </thead>
      <tbody>{rows.map(row => <tr key={row.id} className="border-t">
        <td className="p-2">{[row.contact?.firstName, row.contact?.lastName].filter(Boolean).join(' ') || 'Contact name not recorded'}</td>
        <td className="p-2">{row.contact?.source || row.deal?.source || 'Not recorded'}</td>
        <td className="p-2">{row.contact?.city || 'Not recorded'}</td>
        <td className="p-2">{row.lastCalledAt ? new Date(row.lastCalledAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'No calls recorded'}</td>
        <td className="p-2">{row.priority}</td>
        <td className="p-2">
          <Link className="btn btn-sm" to="/telecaller" state={{ queueLeadId: row.id }}>Call</Link>
        </td>
      </tr>)}</tbody>
    </table>
  </div>}</Section>;
}
export function Today({ rows }: { rows: MyDay['today']; }) {
  const sorted = [...rows].sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.id.localeCompare(b.id));
  return <Section title="Today" to="/follow-ups">{sorted.length === 0 ? <p>No calls, follow-ups or meetings today.</p> : <ol aria-label="Today's timeline" className="space-y-3">{sorted.map(row => <li key={row.id}>
    <Link to={row.to} className="block">
      <span className="font-medium">{time(row.at)} · {row.kind}</span>
      <span className="block text-sm text-[var(--ink-3)]">{row.name || 'Name not recorded'}</span>
    </Link>
  </li>)}</ol>}</Section>;
}
export function MyProposals({ rows }: { rows: MyDay['proposals']; }) {
  return <Section title="My proposals" to="/proposals">{rows.length === 0 ? <p>No open proposals.</p> : <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr>{['Proposal', 'Customer', 'Amount', 'Status'].map(h => <th key={h} className="text-left p-2">{h}</th>)}</tr>
      </thead>
      <tbody>{rows.map(p => <tr key={p.id} className="border-t">
        <td className="p-2">
          <Link to="/proposals" className="underline">{p.number}</Link>{p.forManager && <small className="block">For my manager</small>}</td>
        <td className="p-2">{p.customer || 'Name not recorded'}</td>
        <td className="p-2">{p.amount === null ? 'Amount not recorded' : rupees(p.amount)}</td>
        <td className="p-2">{p.status}</td>
      </tr>)}</tbody>
    </table>
  </div>}</Section>;
}
export function OutstandingList({ data }: { data: Outstanding; }) {
  return <Section title="My outstanding" to="/my-outstanding">{data.rows.length === 0 ? <p>No unpaid orders.</p> : <ul className="space-y-3">{data.rows.map(row => <li key={row.id}>
    <Link to="/my-outstanding" className="block">
      <span>{row.number} · {row.customer || 'Name not recorded'}</span>
      <span className="block text-sm">{rupees(row.balance)} remaining</span>
    </Link>
  </li>)}</ul>}</Section>;
}
